import * as m from '@/paraglide/messages';
import type { Voice } from '@listening/api-contract/legacy-client-types';
import { cn } from '@listening/utils/react-helpers';
import IconPause from '@listening/assets/astro/icons/icon-pause-white.svg?react';
import IconPlay from '@listening/assets/astro/icons/icon-play.svg?react';
import VoiceIcon from '@listening/assets/astro/icons/icon-voices.svg?react';
import voiceDemo from '@listening/assets/astro/images/new-design-25/features-page/voiceDemo-features.png';

import { Button } from '@listening/frontend/components/radix/button';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useVoicesList } from '@listening/frontend/query/voices';
import { Effect, Random } from 'effect';
import { rootLogger } from '@/lib/root-logger';

const moduleLogger = rootLogger.child({ module: 'FeaturesVoiceDemo' });

type AudioBarsProps = {
  isPlaying: boolean;
  voiceName: string;
  onComplete: () => void;
};

// Get first 6 voices from voicesMetadataMap

// const voices: Voice[] = voiceNames.map((voiceName) => {
//   const voiceData = voicesMetadataMap[voiceName];
//   const capitalizedSoftness =
//     voiceData.softness.charAt(0).toUpperCase() + voiceData.softness.slice(1);
//   const description = `${voiceData.gender === 'male' ? 'Male' : 'Female'} • ${capitalizedSoftness}`;

//   return {
//     name: voiceName,
//     avatarSrc: voiceNameToAssetsMap[voiceName].imgSrc,
//     description,
//     audioSrc: voiceNameToAssetsMap[voiceName].audioSrc,
//   };
// });

const AudioBars = ({ isPlaying, voiceName, onComplete }: AudioBarsProps) => {
  const [activeBarIndex, setActiveBarIndex] = useState(-1);
  const [heights] = useState(() =>
    Effect.runSync(Effect.replicateEffect(Random.nextBetween(4, 16), 24)),
  );
  const animationRef = useRef<number | null>(null);
  const lastTimeRef = useRef(0);
  const progressIntervalRef = useRef(300); // ms between bar updates

  if (!isPlaying && activeBarIndex !== -1) setActiveBarIndex(-1);

  useEffect(() => {
    if (!isPlaying) return;

    // Animation function for smoother bar updates
    const animate = (time: number) => {
      if (
        !lastTimeRef.current ||
        time - lastTimeRef.current >= progressIntervalRef.current
      ) {
        lastTimeRef.current = time;
        setActiveBarIndex((prev) => {
          if (prev >= 23) {
            return -1;
          }
          return prev + 1;
        });
      }
      animationRef.current = requestAnimationFrame(animate);
    };

    // Start animation
    lastTimeRef.current = 0;
    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [isPlaying]);

  // Reset when playback completes
  useEffect(() => {
    if (activeBarIndex === 23) {
      const timeout = setTimeout(() => {
        onComplete();
      }, 300);
      return () => {
        clearTimeout(timeout);
      };
    }
  }, [activeBarIndex, onComplete]);

  return (
    <div
      aria-label={`Audio visualization for ${voiceName}`}
      aria-valuemax={24}
      aria-valuemin={0}
      aria-valuenow={activeBarIndex + 1}
      className='flex h-4 items-center'
      role='progressbar'
    >
      {heights.map((height, index) => (
        <div
          aria-hidden='true'
          className={`w-px rounded-full transition-colors duration-300 ${
            isPlaying && index <= activeBarIndex
              ? 'bg-blue-500'
              : 'bg-steel-140'
          }`}
          key={`${voiceName}-bar-${String(index)}`}
          style={{
            height: `${String(height)}px`,
            marginRight: index === heights.length - 1 ? '0' : '1px',
          }}
        />
      ))}
    </div>
  );
};

function FeaturesVoiceDemo() {
  const [playingVoice, setPlayingVoice] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const currentVoiceRef = useRef<Voice | null>(null);
  const voiceOptionsQuery = useVoicesList();
  const voiceOptions = useMemo(
    () => (voiceOptionsQuery.data ?? []).slice(0, 6),
    [voiceOptionsQuery.data],
  );

  const handleAudioEnded = useCallback(() => {
    setPlayingVoice(null);
  }, []);

  // Handle audio playback
  useEffect(() => {
    // Clean up previous audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.removeEventListener('ended', handleAudioEnded);
      audioRef.current = null;
    }

    // If there's a voice to play, create and play the audio
    if (playingVoice) {
      const selectedVoice = voiceOptions.find(
        (voice) => voice.name === playingVoice,
      );
      if (selectedVoice) {
        currentVoiceRef.current = selectedVoice;
        audioRef.current = new Audio(selectedVoice.sample_url);
        audioRef.current.addEventListener('ended', handleAudioEnded);
        audioRef.current.play().catch((error: unknown) => {
          moduleLogger.error({ err: error }, 'Error playing audio');
          setPlayingVoice(null);
        });
      }
    }

    // Cleanup function
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.removeEventListener('ended', handleAudioEnded);
      }
    };
  }, [playingVoice, voiceOptions, handleAudioEnded]);

  const togglePlayPause = (voiceName: string) => {
    if (playingVoice === voiceName) {
      setPlayingVoice(null);
    } else {
      setPlayingVoice(voiceName);
    }
  };

  return (
    <section
      aria-labelledby='voice-demo-title'
      className='mx-auto flex max-w-[1600px] flex-col px-4 py-12 sm:px-6 lg:px-8'
    >
      <div className='relative'>
        {/* Background overlay */}
        <div aria-hidden='true' className='absolute inset-0 -z-10 bg-white' />

        <div className='flex flex-col items-center justify-between gap-8 lg:flex-row lg:gap-16'>
          <div className='flex-1'>
            <h2
              className='text-2xl leading-tight font-bold lg:text-6xl'
              id='voice-demo-title'
            >
              {m.features_voice_demo_title()}
            </h2>
          </div>
          <div className='flex flex-1 justify-end'>
            <p className='max-w-lg text-navy-220/70 sm:text-lg'>
              {m.features_voice_demo_description()}
            </p>
          </div>
        </div>

        <div className='mt-12 flex w-full flex-col gap-6 lg:flex-row'>
          <div
            aria-label='Voice demo interface preview'
            className='mx-auto sm:h-[400px] sm:w-[500px] md:h-[500px] md:w-[600px] lg:mx-0 lg:size-auto lg:flex-1'
            role='img'
          >
            <img
              alt='Voice demo interface preview'
              aria-hidden='true'
              className='size-full rounded-3xl object-contain'
              src={voiceDemo}
            />
          </div>

          <div
            aria-label='Available voice samples'
            className='flex-1 rounded-3xl bg-linear-to-b from-white to-neutral-105 p-6'
            role='region'
          >
            <div className='mb-6 flex items-center justify-center gap-2'>
              <VoiceIcon className='size-5' />
              <span className='text-sm font-medium text-navy-220'>
                {m.common_voices()}
              </span>
            </div>

            <div className='mx-auto flex max-w-[270px] flex-col gap-12'>
              {voiceOptions.map((voice, index) => (
                <div
                  aria-label={`${voice.name} voice sample`}
                  className={cn(
                    'grid w-full grid-cols-[auto_1fr_auto] items-center gap-6 rounded-[500px] border-[5px] border-steel-140 bg-white py-2 pr-4 pl-3 shadow-sm transition-all duration-200 hover:border-steel-220 hover:shadow-md',
                    index % 3 === 0
                      ? 'mr-auto sm:-ml-28 md:-ml-52 lg:mr-auto lg:-ml-28 xl:-ml-40'
                      : index % 3 === 1
                        ? 'sm:-mr-28 sm:ml-auto md:-mr-52 md:ml-auto lg:-mr-28 lg:ml-auto xl:-mr-40'
                        : 'lg:mx-auto xl:mx-auto',
                  )}
                  key={voice.name}
                  role='article'
                >
                  {/* Column 1: Avatar & Name */}
                  <div className='flex items-center gap-2 sm:gap-3'>
                    <img
                      alt={`${voice.name} avatar`}
                      className='size-7 rounded-full sm:size-8'
                      src={voice.image_url}
                    />
                    <span className='text-sm font-medium whitespace-nowrap sm:text-base'>
                      {voice.name}
                    </span>
                  </div>

                  {/* Column 2: Audio bars */}
                  <AudioBars
                    isPlaying={playingVoice === voice.name}
                    onComplete={() => {
                      setPlayingVoice(null);
                    }}
                    voiceName={voice.name}
                  />

                  {/* Column 3: Play/Pause button */}
                  <Button
                    aria-label={`${playingVoice === voice.name ? 'Pause' : 'Play'} ${voice.name} voice sample`}
                    aria-pressed={playingVoice === voice.name}
                    className='size-6 rounded-full bg-steel-700 p-0 hover:scale-110 hover:bg-steel-700/90 active:scale-95'
                    onClick={() => {
                      togglePlayPause(voice.name);
                    }}
                    size='icon'
                  >
                    {playingVoice === voice.name ? (
                      <IconPause
                        aria-hidden='true'
                        className='size-2 text-white'
                      />
                    ) : (
                      <IconPlay
                        aria-hidden='true'
                        className='size-3 text-white'
                      />
                    )}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default FeaturesVoiceDemo;
